import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@/components/buttons/button";
import useTiltifyConfig from "@/hooks/useTiltifyConfig";
import { isDevMode, isTestMode } from "@/util/const";
import { isTiltifyTokenExpired, refreshTiltifyToken } from "@/util/tiltifyHelper.ts";
import type { TiltifyOAuthCallbackUser } from "./tiltify-oauth-button";
import { TiltifyOAuthButton } from "./tiltify-oauth-button";

interface TiltifyTokens {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: string | number;
  createdAt?: string;
}

interface TiltifyUserInfo {
  userID: string;
  username: string;
  slug: string;
}

interface TiltifySetupProps {
  onSignIn?: (data: TiltifyUserInfo) => void;
  onSignOut?: () => void;
}

export default function TiltifySetup({
  onSignIn = (_: unknown) => {},
  onSignOut = () => {},
}: TiltifySetupProps) {
  const { config, setField, clearField } = useTiltifyConfig();
  const tiltifyUsername = config.tiltifyUsername || localStorage.getItem("tiltifyUsername");
  const tiltifyUserSlug = config.tiltifyUserSlug || localStorage.getItem("tiltifyUserSlug");

  const isDevImpersonating = isDevMode && config.tiltifyDevImpersonation === true;

  const [isTiltifySetup, setTiltifySetup] = useState(
    (tiltifyUsername !== null &&
      tiltifyUsername.length > 0 &&
      tiltifyUserSlug !== null &&
      tiltifyUserSlug.length > 0) ||
      isDevImpersonating,
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Guards against re-entering the refresh effect while a request is already
   * in flight. `isRefreshing` drives the UI but cannot be used for this: it is
   * state, so reading it inside the effect would require it as a dependency
   * and its update is what re-renders us in the first place. A ref is written
   * synchronously and is therefore already `true` by the time any re-run of
   * the effect observes it.
   */
  const refreshInFlightRef = useRef(false);

  /**
   * Clears all Tiltify user, campaign, and token data from the reactive config
   * and localStorage, then resets the component to the logged-out state.
   */
  const logoutTiltify = useCallback(() => {
    clearField("tiltifyUserSlug");
    clearField("tiltifyUsername");
    clearField("tiltifyUserID");
    clearField("tiltifyCampaignID");
    clearField("tiltifyPollID");
    clearField("tiltifyCampaignSlug");
    clearField("tiltifyCampaignGoal");
    clearField("tiltifyCampaignName");
    clearField("tiltifyAccessToken");
    clearField("tiltifyRefreshToken");
    clearField("tiltifyTokenExpiresIn");
    clearField("tiltifyTokenCreatedAt");
    clearField("tiltifyDevImpersonation");

    setTiltifySetup(false);
    onSignOut();
  }, [clearField, onSignOut]);

  /**
   * Persists a refreshed token set into the reactive config and localStorage.
   *
   * @param {{ accessToken: string, refreshToken: string, expiresIn: string|number, createdAt: string }} tokens
   */
  const saveTokens = useCallback(
    (tokens: TiltifyTokens | null | undefined) => {
      setField("tiltifyAccessToken", tokens?.accessToken ?? "");
      setField("tiltifyRefreshToken", tokens?.refreshToken ?? "");
      setField("tiltifyTokenExpiresIn", String(tokens?.expiresIn ?? ""));
      setField("tiltifyTokenCreatedAt", tokens?.createdAt ?? "");
    },
    [setField],
  );

  /**
   * Read the stored token fields once per render.
   *
   * These are deliberately hoisted out of the effect below so they can be used
   * as its dependencies. Depending on the whole `config` object instead would
   * re-run the effect on every unrelated `setField` call anywhere in the
   * provider, because `useTiltifyConfigState` spreads a brand-new object on
   * each update (`{ ...prev, [key]: value }`).
   */
  const storedRefreshToken =
    (config.tiltifyRefreshToken || localStorage.getItem("tiltifyRefreshToken")) ?? "";
  const storedAccessToken =
    (config.tiltifyAccessToken || localStorage.getItem("tiltifyAccessToken")) ?? "";
  const storedCreatedAt =
    (config.tiltifyTokenCreatedAt || localStorage.getItem("tiltifyTokenCreatedAt")) ?? "";
  const storedExpiresIn =
    (config.tiltifyTokenExpiresIn || localStorage.getItem("tiltifyTokenExpiresIn")) ?? "";

  /**
   * On mount (and whenever the stored tokens change), validate the stored
   * Tiltify tokens:
   *
   *  1. If no refresh token is present the session cannot be recovered → sign out.
   *  2. If the access token is absent or within 5 minutes of expiry, attempt a
   *     silent refresh using the stored refresh token.
   *  3. If the refresh call fails or returns nothing → sign out.
   *
   * Refresh tokens are single-use (Tiltify rotates them), so this must fire at
   * most once per token. `refreshInFlightRef` enforces that here, and
   * `@playlive/fundraiser-auth` de-duplicates concurrent exchanges of the same
   * token as a backstop for other mounted consumers.
   */
  useEffect(() => {
    if (!isTiltifySetup || isTestMode || isDevImpersonating) {
      return;
    }

    // A refresh is already running — its completion will update the stored
    // tokens and re-run this effect with fresh values.
    if (refreshInFlightRef.current) {
      return;
    }

    // No refresh token → no way to recover the session.
    if (!storedRefreshToken) {
      logoutTiltify();
      return;
    }

    // If the access token is missing or expiring soon, refresh silently.
    if (!storedAccessToken || isTiltifyTokenExpired(storedCreatedAt, storedExpiresIn)) {
      refreshInFlightRef.current = true;
      setIsRefreshing(true);
      refreshTiltifyToken(storedRefreshToken)
        .then((user) => {
          if (!user) {
            logoutTiltify();
            return;
          }
          saveTokens(user.tokens as TiltifyTokens | null | undefined);
        })
        .catch(() => logoutTiltify())
        .finally(() => {
          refreshInFlightRef.current = false;
          setIsRefreshing(false);
        });
    }
  }, [
    isTiltifySetup,
    isDevImpersonating,
    storedRefreshToken,
    storedAccessToken,
    storedCreatedAt,
    storedExpiresIn,
    logoutTiltify,
    saveTokens,
  ]);

  if (!isTiltifySetup) {
    return (
      <TiltifyOAuthButton
        setTiltifyUser={(user: TiltifyOAuthCallbackUser) => {
          setField("tiltifyUserSlug", user.slug);
          setField("tiltifyUserID", user.id);
          setField("tiltifyUsername", user.username);

          saveTokens(user.tokens);

          setTiltifySetup(user.username.length > 0 && user.slug.length > 0 && user.id.length > 0);

          onSignIn({
            userID: user.id,
            username: user.username,
            slug: user.slug,
          });
        }}
      />
    );
  }

  if (isRefreshing) {
    return (
      <div className="flex items-center gap-2 min-h-10">
        <span className="loading loading-spinner loading-sm" />
        <span className="text-sm">Reconnecting to Tiltify…</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-10">
      <div className="flex items-center justify-between">
        <article className="prose text-sm">
          {isDevImpersonating ? (
            <>
              <span className="badge badge-warning badge-sm mr-2">DEV</span>
              Impersonating <b>{config.tiltifyUsername}</b>
              {config.tiltifyUserSlug ? (
                <span className="opacity-70"> ({config.tiltifyUserSlug})</span>
              ) : null}
            </>
          ) : (
            <>
              Your Tiltify account, <b>{isTestMode ? "Test User" : config.tiltifyUsername}</b>, has
              been connected
            </>
          )}
        </article>

        <Button
          style="accent"
          mode="button"
          disabled={isTestMode}
          onClick={logoutTiltify}
          size="small"
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
